import { useState } from 'react';
import { PropersAssignedPreSaleVisits } from '../models/dashboardModels';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Table,
} from 'reactstrap';
import {
  IconCalendar,
  IconChevronRight,
  IconClock,
  IconHeart,
  IconMail,
  IconMapPin,
  IconPhone,
} from '@tabler/icons-react';
import VisitMarketplaceTypeBadge from '../../../components/VisitMarketplaceTypeBadge';
import {
  getBadgeColorVisitPreSaleStatusAdapter,
  getVisitPreSaleStatusAdapter,
} from '../../properties/adapters/visitsAdapter';
import FavoritesViewButton from './FavoritesViewButton';
import IntentFactBanner from './IntentFactBanner';
import IntentSignalsCell from './IntentSignalsCell';
import InvestorFavoritesModal from './InvestorFavoritesModal';

export interface Props {
  visits: PropersAssignedPreSaleVisits[];
  footer?: any;
  selectedVisitId?: string | null;
  onSelectVisit?: (visitId: string) => void;
}
const DashboardPreSaleVisitManagerListTable = ({
  visits,
  footer,
  selectedVisitId,
  onSelectVisit,
}: Props) => {
  const [favoritesModalVisitId, setFavoritesModalVisitId] = useState<
    string | null
  >(null);

  const navigate = useNavigate();

  const favoritesModalVisit = visits.find(
    (visit) => visit.id === favoritesModalVisitId,
  );

  return (
    <Card className="dv2-table">
      <CardHeader>
        <CardTitle>Asesorias</CardTitle>
      </CardHeader>
      <CardBody>
        <IntentFactBanner isPreSale />
        <div className="table-responsive">
          <Table striped hover>
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Inversionista</th>
                <th>Proper/SDR</th>
                <th style={{ width: 260, maxWidth: 260 }}>
                  Señales de intención
                </th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visits.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No hay asesorías registradas.
                  </td>
                </tr>
              )}
              {visits.map((visit) => (
                <tr
                  key={visit.id}
                  className={visit.id === selectedVisitId ? 'is-selected' : ''}
                  onClick={() => onSelectVisit?.(visit.id)}
                >
                  <td>
                    <div>
                      <div className="font-weight-bold">
                        [{visit.projectInfo.code}] {visit.projectInfo.name}
                      </div>
                      <div className="d-flex align-items-center text-muted small">
                        <IconMapPin size={12} className="me-1" />
                        {visit.projectInfo.address}
                      </div>
                      <div className="text-success font-weight-bold">
                        Unidades desde {visit.projectInfo.range_price}
                      </div>
                      <VisitMarketplaceTypeBadge
                        marketplace_type={visit.visitInfo.marketplace_type}
                      />
                      {visit.projectInfo.is_favorite && (
                        <Badge color="danger" className="ml-2">
                          <IconHeart size={12} className="me-1" />
                          En sus favoritos
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="font-weight-bold">
                        {visit.visitInfo.investor_name}
                      </div>
                      <div className="d-flex align-items-center text-muted">
                        <IconMail size={12} className="me-1" />{' '}
                        {visit.visitInfo.investor_email}
                      </div>
                      <div className="d-flex align-items-center text-muted">
                        <IconPhone size={12} className="me-1" />
                        {visit.visitInfo.investor_phone}
                      </div>
                      <div className="d-flex align-items-center font-weight-bold">
                        <IconCalendar size={12} className="me-1" />
                        {visit.visitInfo.visit_date}
                      </div>
                      <div className="d-flex align-items-center font-weight-bold">
                        <IconClock size={12} className="me-1" />
                        {visit.visitInfo.visit_time}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="font-weight-bold">
                        (Proper) {visit.properInfo.name}
                      </div>
                      <div className="font-weight-bold">
                        (SDR) {visit.sdrInfo.name}
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      width: 260,
                      maxWidth: 260,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    <IntentSignalsCell
                      favorites={visit.visitInfo.investor_favorites}
                      visitDate={visit.visitInfo.visit_date}
                      visitTime={visit.visitInfo.visit_time}
                      status={visit.visitInfo.status}
                      onViewFavorites={() =>
                        setFavoritesModalVisitId(visit.id)
                      }
                      isPreSale
                    />
                  </td>
                  <td>
                    <Badge
                      className="badge-lg mr-2"
                      color={getBadgeColorVisitPreSaleStatusAdapter(
                        visit.visitInfo.status,
                      )}
                    >
                      {getVisitPreSaleStatusAdapter(visit.visitInfo.status)}
                    </Badge>
                    <FavoritesViewButton
                      favorites={visit.visitInfo.investor_favorites}
                      onClick={() => setFavoritesModalVisitId(visit.id)}
                    />
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <button
                        type="button"
                        className="dv2-detail-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/dashboard/presale-visits/${visit.id}`, {
                            state: { visit },
                          });
                        }}
                      >
                        Ver detalle
                        <IconChevronRight size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>
      <CardFooter>{footer && <div>{footer}</div>}</CardFooter>
      {favoritesModalVisit && (
        <InvestorFavoritesModal
          isOpen={!!favoritesModalVisitId}
          toggle={() => setFavoritesModalVisitId(null)}
          investorName={favoritesModalVisit.visitInfo.investor_name}
          favorites={favoritesModalVisit.visitInfo.investor_favorites || []}
          currentListingId={favoritesModalVisit.projectInfo.id}
        />
      )}
    </Card>
  );
};

export default DashboardPreSaleVisitManagerListTable;
